
import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { getSellerActiveCoupons } from '../../../../services/couponAdminApi';
import { useToast } from '../../../../hooks/use-toast';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../ui/card';
import { Alert, AlertDescription } from '../../../ui/alert';
import { Badge } from '../../../ui/badge';

const ActiveCouponsSection = forwardRef(({ sellerProfileId }, ref) => {
    const { toast } = useToast();
    const [activeCoupons, setActiveCoupons] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchCoupons = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await getSellerActiveCoupons(sellerProfileId);
            setActiveCoupons(res.data);
            setError('');
        } catch (err) {
            const errorMsg = err.response?.data?.detail || 'Failed to fetch active coupons.';
            setError(errorMsg);
            toast({ variant: 'destructive', title: 'Error', description: errorMsg });
        } finally {
            setIsLoading(false);
        }
    }, [sellerProfileId, toast]);

    useEffect(() => {
        fetchCoupons();
    }, [fetchCoupons]);

    useImperativeHandle(ref, () => ({
        refetch: fetchCoupons,
    }), [fetchCoupons]);

    const formatToIST = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('en-IN', {
            timeZone: 'Asia/Kolkata',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        });
    };

    if (isLoading) return <div className="p-4">Loading active coupons...</div>;

    return (
        <Card className="border-none shadow-none">
            <CardHeader>
                <CardTitle>Active Coupons</CardTitle>
                <CardDescription>
                    This list shows all coupons currently active and available for this seller to use.
                </CardDescription>
            </CardHeader>
            <CardContent>
                {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
                
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Coupon Code</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Activated On (IST)</TableHead>
                            <TableHead>Expires On (IST)</TableHead>
                            <TableHead>Source</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {activeCoupons.length > 0 ? (
                            activeCoupons.map((coupon, index) => (
                                <TableRow key={index}>
                                    <TableCell><Badge variant="secondary">{coupon.coupon_code}</Badge></TableCell>
                                    <TableCell className="text-sm text-muted-foreground">{coupon.coupon_description}</TableCell>
                                    <TableCell>{formatToIST(coupon.activated_at)}</TableCell>
                                    <TableCell>{formatToIST(coupon.expires_at)}</TableCell>
                                    <TableCell>
                                        {coupon.activation_source === 'ADMIN' 
                                            ? `Admin (${coupon.activating_admin_email || 'N/A'})` 
                                            : coupon.activation_source
                                        }
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan="5" className="text-center text-muted-foreground">
                                    No active coupons found for this seller.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
});

ActiveCouponsSection.displayName = 'ActiveCouponsSection';

export default ActiveCouponsSection;